import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import { Save, Settings } from '@mui/icons-material';
import { BsArrowLeftShort } from 'react-icons/bs';
import { getConfig, updateConfig } from '../api';
import useAuth from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import ModalHeader from '../components/ModalHeader';

function ConfigManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [config, setConfig] = useState({
    SYNTELESTIS_PAGIOU_KOSTOUS_DT: 0.60,
    COST_ORGANIKA_120: 0.00,
    COST_ORGANIKA_240: 0.00,
    COST_ORGANIKA_660: 0.00,
    COST_ORGANIKA_1100: 0.00,
    COST_SYMMEIKTA_120: 7.00,
    COST_SYMMEIKTA_240: 7.00,
    COST_SYMMEIKTA_660: 7.00,
    COST_SYMMEIKTA_1100: 7.00
  });

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      setError('Δεν έχετε δικαίωμα πρόσβασης. Μόνο διαχειριστές επιτρέπονται.');
      setTimeout(() => {
        navigate('/select');
      }, 2000);
      return;
    }

    // Fetch current configuration
    fetchConfig();
  }, [user, navigate]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await getConfig();
      setConfig(data);
      setError('');
    } catch (err) {
      console.error('Error fetching config:', err);
      setError('Σφάλμα κατά τη φόρτωση των παραμέτρων');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Convert to number and ensure non-negative
    const numValue = parseFloat(value);
    if (value === '' || (!isNaN(numValue) && numValue >= 0)) {
      setConfig(prev => ({
        ...prev,
        [field]: value === '' ? '' : numValue
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Validate all fields are filled and non-negative
      const hasEmptyFields = Object.values(config).some(val => val === '' || val === null || val === undefined);
      if (hasEmptyFields) {
        setError('Παρακαλώ συμπληρώστε όλα τα πεδία');
        return;
      }

      const hasNegativeValues = Object.values(config).some(val => parseFloat(val) < 0);
      if (hasNegativeValues) {
        setError('Οι τιμές δεν μπορούν να είναι αρνητικές');
        return;
      }

      setSaving(true);
      await updateConfig(config);
      setSuccess('Οι παράμετροι ενημερώθηκαν επιτυχώς!');
      setError('');
      
      // Refresh config from server
      await fetchConfig();
    } catch (err) {
      console.error('Error updating config:', err);
      if (err.response?.status === 403) {
        setError('Δεν έχετε δικαίωμα ενημέρωσης. Μόνο διαχειριστές επιτρέπονται.');
      } else {
        setError('Σφάλμα κατά την ενημέρωση των παραμέτρων');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <ModalHeader title="Ρυθμίσεις Κόστους" subtitle="Διαχείριση Παραμέτρων Συστήματος" />
      
      {/* Back button */}
      <div className='items-center justify-between mx-auto mb-4 mt-2 lg:flex lg:px-0'>
        <Link to={"/select"} className="flex text-app_green_light shadow-md font-bold rounded-full text-lg px-10 py-2 text-center">
          <BsArrowLeftShort className='self-center w-6 h-6 text-app_green_light' /> Επιστροφή
        </Link>
      </div>

      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Settings sx={{ fontSize: 40, color: '#6fa84a', mr: 2 }} />
              <Typography variant="h4" component="h1" fontWeight="bold" color="#6fa84a">
                Παράμετροι Κόστους Αποκομιδής
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={3}>
              {/* Fixed Cost Coefficient */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                  Συντελεστής Παγίου Κόστους
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Συντελεστής Παγίου Κόστους ΔΤ (%)"
                  type="number"
                  value={config.SYNTELESTIS_PAGIOU_KOSTOUS_DT}
                  onChange={(e) => handleInputChange('SYNTELESTIS_PAGIOU_KOSTOUS_DT', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  helperText="Συντελεστής που εφαρμόζεται στο κόστος Τ.Μ. (π.χ. 0.60 = 60%)"
                />
              </Grid>

              {/* Organika Costs */}
              <Grid item xs={12} mt={3}>
                <Typography variant="h6" gutterBottom sx={{ color: '#8d6e63' }} fontWeight="bold">
                  Κόστη Οργανικών (€ ανά αποκομιδή)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 120L"
                  type="number"
                  value={config.COST_ORGANIKA_120}
                  onChange={(e) => handleInputChange('COST_ORGANIKA_120', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 240L"
                  type="number"
                  value={config.COST_ORGANIKA_240}
                  onChange={(e) => handleInputChange('COST_ORGANIKA_240', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 660L"
                  type="number"
                  value={config.COST_ORGANIKA_660}
                  onChange={(e) => handleInputChange('COST_ORGANIKA_660', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 1100L"
                  type="number"
                  value={config.COST_ORGANIKA_1100}
                  onChange={(e) => handleInputChange('COST_ORGANIKA_1100', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>

              {/* Symmeikta Costs */}
              <Grid item xs={12} mt={3}>
                <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }} fontWeight="bold">
                  Κόστη Συμμείκτων (€ ανά αποκομιδή)
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 120L"
                  type="number"
                  value={config.COST_SYMMEIKTA_120}
                  onChange={(e) => handleInputChange('COST_SYMMEIKTA_120', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 240L"
                  type="number"
                  value={config.COST_SYMMEIKTA_240}
                  onChange={(e) => handleInputChange('COST_SYMMEIKTA_240', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 660L"
                  type="number"
                  value={config.COST_SYMMEIKTA_660}
                  onChange={(e) => handleInputChange('COST_SYMMEIKTA_660', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  label="Κάδος 1100L"
                  type="number"
                  value={config.COST_SYMMEIKTA_1100}
                  onChange={(e) => handleInputChange('COST_SYMMEIKTA_1100', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>

              {/* Save Button */}
              <Grid item xs={12} mt={3}>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(to right, #6fa84a, #49a3d4)',
                      '&:hover': {
                        background: 'linear-gradient(to right, #5d8f3e, #3a8ab5)',
                      }
                    }}
                  >
                    {saving ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card elevation={2} sx={{ mt: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              <strong>Σημείωση:</strong> Οι παράμετροι αυτοί χρησιμοποιούνται για τον υπολογισμό των θεωρητικών 
              κοστών αποκομιδής βάσει συχνοτήτων. Οι αλλαγές εφαρμόζονται άμεσα σε όλους τους υπολογισμούς.
              <br /><br />
              • Ο συντελεστής παγίου κόστους εφαρμόζεται στα τετραγωνικά μέτρα<br />
              • Όλες οι τιμές πρέπει να είναι μη αρνητικοί αριθμοί
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ConfigManagement;
